import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { LineReveal } from "./Motion";
import { BUSINESS } from "../config";

const HERO_IMG = "https://images.unsplash.com/photo-1612145342813-849b0c3e7f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden bg-[#050505]">
      {/* parallax bg */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Junk hauling crew loading a truck" className="w-full h-[115%] object-cover opacity-40 img-editorial" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
      </motion.div>

      <div className="absolute inset-0 grid-lines opacity-[0.35] z-[1]" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10 pt-40 md:pt-52 pb-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 mb-8">
          <span className="h-px w-12 bg-[var(--accent)]" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--text-secondary)]">{BUSINESS.city} · Est. {BUSINESS.founded}</span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.82] tracking-tighter text-[18vw] md:text-[13vw] lg:text-[11vw]">
          <LineReveal lines={["We haul", "your junk—"]} />
          <span className="block text-[var(--accent)]">
            <LineReveal lines={["you keep"]} delay={0.36} />
          </span>
          <LineReveal lines={["the space."]} delay={0.48} />
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="max-w-md text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
            Full-service junk removal, estate cleanouts, and resale across {BUSINESS.serviceArea}. Snap a photo, get a same-day bid.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="flex flex-wrap items-center gap-4">
            <button data-testid="hero-bid-btn" onClick={() => document.querySelector("#bid")?.scrollIntoView({ behavior: "smooth" })} className="btn-wipe bg-[var(--accent)] text-white hover:text-white px-8 py-4 font-mono text-sm uppercase tracking-widest font-bold">
              Get an instant bid
            </button>
            <a data-testid="hero-call-btn" href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 border border-[var(--border-dim)] hover:border-white px-8 py-4 font-mono text-sm uppercase tracking-widest transition-colors">
              <Phone size={15} /> {BUSINESS.phone}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[var(--text-secondary)]">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
